<template>
  <div class="complete-order-wrapper d-flex fl-s-b" data-cy="payment-step">
    <div class="booking-left-side d-flex fl-col fl-s-b">
      <div>
        <div
          class="mb-2.5"
          v-for="paymentSetting in paymentSettings"
          :key="paymentSetting.id"
        >
          <SButton
            type="button"
            :id="paymentSetting.id"
            class="w-full payment-option"
            :class="{
              selected:
                paymentMethod ===
                  paymentMethodToOrderPaymentMethod(paymentSetting.id) &&
                step === BookingStep.Payment &&
                !isFree,
              'disabled-payment-option':
                disablePaymentMethod(
                  paymentMethodToOrderPaymentMethod(paymentSetting.id)
                ) ||
                step === BookingStep.Refund ||
                promocodeBeingChecked,
            }"
            @click="updatePaymentMethod(paymentSetting.id)"
            :disabled="
              disablePaymentMethod(
                paymentMethodToOrderPaymentMethod(paymentSetting.id)
              ) ||
              isFree ||
              isFullyPaidInPromocodeStep ||
              isFullyPaidInDiscountStep ||
              step === BookingStep.Refund ||
              promocodeBeingChecked
            "
            data-cy="payment-method"
          >
            {{
              PaymentMethodToTranslatedString[
                paymentMethodToOrderPaymentMethod(paymentSetting.id)
              ]
            }}
          </SButton>
        </div>

        <div class="mb-2.5" v-if="showDiscount">
          <SButton
            v-if="showDiscountButton"
            type="button"
            class="w-full payment-option"
            :class="{
              selected: step === BookingStep.Discount,
              'disabled-payment-option':
                disablePaymentMethod(PaymentMethod.POS_CARD) ||
                step === BookingStep.Refund ||
                promocodeBeingChecked,
            }"
            :disabled="
              disablePaymentMethod(PaymentMethod.POS_CARD) ||
              step === BookingStep.Refund ||
              promocodeBeingChecked
            "
            data-cy="payment-method"
            @click="displayDiscounts"
          >
            {{ $t("button.discount") }}
          </SButton>
        </div>
        <div class="mb-2.5" v-if="step !== BookingStep.Refund">
          <SButton
            type="button"
            class="w-full payment-option"
            :class="{
              selected: step === BookingStep.Promocode,
            }"
            data-cy="payment-method"
            @click="displayPromocodeArea"
          >
            Promocode
          </SButton>
        </div>

        <div class="mb-2.5" v-if="step !== BookingStep.Refund">
          <SButton
            type="button"
            class="w-full payment-option"
            :class="{
              selected: step === BookingStep.PromocodeCampaigns,
            }"
            data-cy="payment-method"
            @click="displayPromocodeCampaigns"
          >
            {{ $t("button.promocode-campaigns") }}
          </SButton>
        </div>

        <div class="mb-2.5" v-if="step !== BookingStep.Refund">
          <SButton
            type="button"
            class="w-full payment-option"
            :class="{
              selected: step === BookingStep.Offers,
              'disabled-payment-option': promocodeBeingChecked,
            }"
            :disabled="promocodeBeingChecked"
            data-cy="payment-method"
            @click="displayOffers"
          >
            {{ $t("offers.offers") }}
          </SButton>
        </div>

        <div class="mb-2.5" v-if="step === BookingStep.Refund">
          <SButton
            type="button"
            variant="destructive"
            class="w-full payment-option selected-warning"
            data-cy="refund-button"
          >
            {{ $t("button.refund") }}
          </SButton>
        </div>
      </div>
    </div>

    <div class="booking-right-side d-flex fl-col">
      <div v-if="step === BookingStep.Refund" class="d-flex fl-col fl-j-c">
        <div class="d-flex flex-row fl-j-c">
          <SButton
            type="button"
            variant="outline"
            class="order-amount default-cursor"
            data-cy="refund-amount"
          >
            {{ whatIsLeftToPayText }}
          </SButton>
        </div>
        <div class="d-flex flex-row fl-j-c">
          <SSelect v-model="refundPaymentMethod">
            <SSelectTrigger class="w-[220px]" data-cy="refund-method-select">
              <SSelectValue />
            </SSelectTrigger>
            <SSelectContent>
              <SSelectItem
                data-cy="refund-option"
                class="refund-option"
                v-for="paymentSetting in refundPaymentMethodOptions"
                :key="paymentSetting.id"
                :value="paymentSetting.id"
                :disabled="isRefundPaymentMethodDisabled(paymentSetting)"
              >
                {{ paymentSetting.label }}
              </SSelectItem>
            </SSelectContent>
          </SSelect>
        </div>
        <div class="d-flex flex-row fl-j-c">
          <SButton
            type="button"
            size="lg"
            class="apply-btn"
            data-cy="apply-refund-button"
            :disabled="isApplyRefundDisabled"
            @click="handleRefund"
          >
            {{ $t("promocode.apply") }}
          </SButton>
        </div>
      </div>
      <div
        v-else-if="
          step !== BookingStep.Discount &&
          step !== BookingStep.Promocode &&
          step !== BookingStep.PromocodeCampaigns &&
          step !== BookingStep.Offers &&
          (!isFree || paymentMethod === PaymentMethod.EXTERNAL_INCERT_VOUCHER)
        "
      >
        <div
          v-if="paymentMethod === PaymentMethod.CASH"
          style="display: flex; flex-direction: column; align-items: center"
        >
          <div
            style="
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              height: 70px;
              width: 50%;
            "
          >
            <div>
              <p style="font-size: 1.125rem">
                {{ t("multicurrencies.cash-payment") }}
              </p>
            </div>
            <div>
              <SButton
                v-if="currencies.length > 1 && hasMultiCurrenciesEnabled"
                type="button"
                variant="link"
                class="h-auto p-0 font-bold"
                data-cy="pay-in-other-currencies-toggle"
                @click="toggleMultiCurrencies"
              >
                {{
                  payInMultiCurrencies
                    ? `${t("multicurrencies.pay-in")} ${bookingPaymentCurrency}`
                    : t("multicurrencies.pay-in-other-currencies")
                }}
              </SButton>
            </div>
          </div>
          <SSeparator
            style="width: 50%; border: 1px solid rgba(0, 0, 0, 0.15)"
          />
          <div
            v-if="payInMultiCurrencies"
            style="
              width: 50%;
              height: 150px;
              display: flex;
              flex-direction: column;
              align-items: start;
              justify-content: end;
              gap: 1rem;
            "
          >
            <div>
              <p class="light-text">
                {{ t("multicurrencies.currency-of-payment") }}
              </p>
            </div>
            <div style="display: flex; justify-content: center; width: 100%">
              <div class="flex gap-2">
                <SButton
                  v-for="currency in currencies.filter(
                    (c: Currency) => c.label !== bookingPaymentCurrency
                  )"
                  :key="currency.id"
                  :data-cy="`pay-in-currency-${currency.label}`"
                  type="button"
                  size="lg"
                  :variant="
                    selectedCashCurrencyLabel === currency.label
                      ? 'default'
                      : 'outline'
                  "
                  @click="selectedCashCurrencyChanged(currency.label)"
                >
                  {{ currency.label }}
                </SButton>
              </div>
            </div>
            <div
              :style="{
                visibility: currency ? 'visible' : 'hidden',
              }"
              style="
                display: flex;
                justify-content: space-between;
                align-items: end;
                width: 100%;
                height: 50px;
              "
            >
              <div>
                <p class="light-text">
                  1 {{ groupCurrency.label }} =
                  {{ currency?.exchangeRate }}
                  {{ currency?.label }}
                </p>
              </div>
              <div>
                <p class="light-text">
                  {{ displayedAmount }}
                </p>
              </div>
            </div>
          </div>
          <el-row style="width: 100%" justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
          <el-row>
            <SButton
              type="button"
              variant="outline"
              class="amount-button"
              data-cy="partial-pay-custom"
              @click="showCalculator"
            >
              {{ $t("button.custom") }}
            </SButton>
            <SButton
              type="button"
              variant="outline"
              class="amount-button"
              data-cy="partial-pay-full"
              @click="setProvidedAmount(whatIsLeftToPay)"
            >
              {{ whatIsLeftToPayText }}
            </SButton>
            <SButton
              type="button"
              variant="outline"
              class="amount-button"
              data-cy="partial-pay-10"
              @click="setProvidedAmount(10)"
            >
              {{ (currency?.label || paymentCurrency) + " " + 10 }}
            </SButton>
          </el-row>
          <el-row>
            <SButton
              type="button"
              variant="outline"
              class="amount-button"
              data-cy="partial-pay-20"
              @click="setProvidedAmount(20)"
            >
              {{ (currency?.label || paymentCurrency) + " " + 20 }}
            </SButton>
            <SButton
              type="button"
              variant="outline"
              class="amount-button"
              data-cy="partial-pay-50"
              @click="setProvidedAmount(50)"
            >
              {{ (currency?.label || paymentCurrency) + " " + 50 }}
            </SButton>
            <SButton
              type="button"
              variant="outline"
              class="amount-button"
              data-cy="partial-pay-100"
              @click="setProvidedAmount(100)"
            >
              {{ (currency?.label || paymentCurrency) + " " + 100 }}
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.POS_ADYEN">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              data-cy="partial-pay-custom"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.BANK_TRANSFER">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.INVOICE">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.TO_BE_INVOICED">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.POS_CARD">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.CHEQUE">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.CHEQUE_VACANCES">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.CHEQUE_CULTURE">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.PASS_CULTURE">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.CHORUS">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.CE">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.OCCYG">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.COUPON_SPORT">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.POS_REKA">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>

        <div v-else-if="paymentMethod === PaymentMethod.BUY_CLUB">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>

        <div v-else-if="paymentMethod === PaymentMethod.POS_BIMPLI_CADO">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>

        <div v-else-if="paymentMethod === PaymentMethod.POS_KADEOS_INFINI">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>

        <div v-else-if="paymentMethod === PaymentMethod.POS_ANCV_CONNECT">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>

        <div v-else-if="paymentMethod === PaymentMethod.POS_DIVERSE_VOUCHER">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.CFF">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.CGN">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.QOQA">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.DEINDEAL">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div v-else-if="paymentMethod === PaymentMethod.REALDEAL">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>

        <div v-else-if="paymentMethod === PaymentMethod.TOUT_UN_CANTON">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>

        <div v-else-if="paymentMethod === PaymentMethod.CHAPLINS">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div>
        <div
          v-else-if="paymentMethod === PaymentMethod.EXTERNAL_INCERT_VOUCHER"
        >
          <IncertVoucher />
        </div>

        <div
          v-else-if="
            paymentMethod === PaymentMethod.Swiss_QR_Code_Bank_Transfer
          "
        >
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
            >
              {{ displayedAmount }}
            </SButton>
          </el-row>
        </div>

        <!-- <div v-else-if="paymentMethod === PaymentMethod.HELLO_CSE">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div> -->

        <!-- <div v-else-if="paymentMethod === paymentMethod.Z_CH_GUTSCHEIN">
          <el-row justify="center">
            <SButton
              type="button"
              variant="outline"
              :class="['order-amount', whatIsLeftToPay ? '' : 'default-cursor']"
              @click="whatIsLeftToPay ? showCalculator() : null"
            >
              {{ displayedAmount }}
              <SvgIcon
                v-if="whatIsLeftToPay"
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </SButton>
          </el-row>
        </div> -->

        <div v-else>
          <el-row justify="center">
            <p>
              {{ t("payment.no-payment-method") }}
            </p>
          </el-row>
        </div>
      </div>
      <div v-else-if="step === BookingStep.Promocode">
        <Promocode />
      </div>
      <div v-else-if="step === BookingStep.Discount">
        <Discount />
      </div>
      <PromocodeCampaigns v-else-if="step === BookingStep.PromocodeCampaigns" />
      <Offers v-else-if="step === BookingStep.Offers" />
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { PaymentMethod } from "@smeetz/order-common";
import { manualPaymentMethods } from "@smeetz/payment-common";
import { useOrderStore } from "@smeetz/order-store";

import { Currencies, Currency } from "@/constants/currency";
import { SvgIcon } from "@/components/index";
import editPenSvg from "@/assets/images/svg/edit-pen.svg";
import {
  SButton,
  SSeparator,
  SSelect,
  SSelectTrigger,
  SSelectValue,
  SSelectContent,
  SSelectItem,
} from "@smeetz/ui-core";

import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";
import { BookingStep } from "@/constants/booking";
import { PaymentMethodTypes } from "@/types/db/pos-config";
import POSConfigModule from "@/store/modules/pos-config";
import Discount from "./Discount.vue";
import Promocode from "./Promocode.vue";
import IncertVoucher from "./IncertVoucher.vue";
import { formatCurrency } from "@/utils/helpers/currency";
import { getLocale } from "@/lang";
import { LogService } from "@/services/logging";
import {
  getDefaultPaymentMethod,
  paymentMethodToOrderPaymentMethod,
  PaymentMethodToTranslatedString,
} from "@/utils/vue-helpers/pos-config";
import CashFloatModule from "@/store/modules/cash-float";
import { hasMultiCurrenciesEnabled } from "@/utils/vue-helpers/cash-float";
import PromocodeCampaigns from "./PromocodeCampaigns.vue";
import Offers from "./Offers.vue";
import UserModule from "@/store/modules/user";
import { syncOrderAvailabilities } from "@/utils/vue-helpers/order-stock.helper";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import {
  canRefundByTerminal,
  getRefundMethodOptions,
  proceedAdyenRefund,
  proceedOnlineRefund,
  proceedRegularRefund,
} from "@/utils/vue-helpers/orders.helper";
import { isPaymentInProgress } from "@/services/payment.service";

export default defineComponent({
  name: "app",
  components: {
    Discount,
    SvgIcon,
    Promocode,
    PromocodeCampaigns,
    IncertVoucher,
    Offers,
    SButton,
    SSeparator,
    SSelect,
    SSelectTrigger,
    SSelectValue,
    SSelectContent,
    SSelectItem,
  },
  setup() {
    const { t } = useI18n({});
    const iconStyle = "filter: contrast(170%) invert(0%); padding-left: 10px;";
    const iconContent = editPenSvg;
    const iconAlt = "edit";

    const orderMetaStore = useOrderMetaStore();
    const { step, paymentMethod, currency, providedAmount, refundMethod } =
      storeToRefs(orderMetaStore);

    const orderStore = useOrderStore();
    const { selectedOrder, selectedOrderSummary } = storeToRefs(orderStore);

    // Order this payment step was opened for
    const paymentOrderReference = selectedOrder.value?.reference;

    const totalBalance = computed(() => {
      return selectedOrderSummary.value.balance;
    });

    const cashPaymentMethod = computed(() => {
      return POSConfigModule.getPaymentConfig.cash.used;
    });

    const bookingPaymentCurrency = computed(() => {
      return selectedOrder.value?.orderSession?.currency as Currencies;
    });

    const paymentCurrency = computed(() => {
      if (paymentMethod.value === PaymentMethod.CASH && currency.value) {
        return currency.value.label;
      }

      return bookingPaymentCurrency.value;
    });

    const cardAutoPaymentMethod = computed(() => {
      return POSConfigModule.getPaymentConfig.cardAuto.used;
    });

    const cardManualPaymentMethod = computed(() => {
      return POSConfigModule.getPaymentConfig.cardManual.used;
    });

    const bankTransferPaymentMethod = computed(() => {
      return POSConfigModule.getPaymentConfig.bankTransfer.used;
    });

    const InvoicePaymentMethod = computed(() => {
      return POSConfigModule.getPaymentConfig.invoice.used;
    });

    const ChequePaymentMethod = computed(() => {
      return (
        POSConfigModule.getPaymentConfig.cheque?.used &&
        (POSConfigModule.getGroupCountryCode === "FR" || getLocale() === "fr")
      );
    });

    const ChequeVacancesPaymentMethod = computed(() => {
      return (
        POSConfigModule.getPaymentConfig.chequeVacances?.used &&
        (POSConfigModule.getGroupCountryCode === "FR" || getLocale() === "fr")
      );
    });

    const CEPaymentMethod = computed(() => {
      return (
        POSConfigModule.getPaymentConfig.CE?.used &&
        groupId.value &&
        [19360, 16342, 16342].includes(groupId.value)
      );
    });

    const OCCYGPaymentMethod = computed(() => {
      return (
        POSConfigModule.getPaymentConfig.OCCYG?.used &&
        groupId.value &&
        [19360, 16342, 16342].includes(groupId.value)
      );
    });

    const isFree = computed(() => {
      // A booking is considered FREE if their tickets are free or is fully discounted
      // return isBookingFree.value;
      return totalBalance.value === 0;
    });

    const isFullyPaidInPromocodeStep = computed(() => {
      // A booking that becomes fully paid in Promocode Step will have payment methods disabled
      return (
        [BookingStep.Promocode, BookingStep.PromocodeCampaigns].includes(
          step.value
        ) && totalBalance.value <= 0
      );
    });

    const isFullyPaidInDiscountStep = computed(() => {
      // A booking that becomes fully paid in Discount Step will have payment methods disabled
      return step.value === BookingStep.Discount && totalBalance.value <= 0;
    });

    const showDiscount = computed(() => {
      const hasDiscountRole = !UserModule.isSellerNoDiscount;
      return (
        hasDiscountRole &&
        (POSConfigModule.getPaymentConfig.cash.used ||
          POSConfigModule.getPaymentConfig.cardAuto.used ||
          POSConfigModule.getPaymentConfig.cardManual.used)
      );
    });

    const groupId = computed(() => {
      return POSConfigModule.groupId;
    });
    const groupCurrencies = computed(() => {
      return POSConfigModule.groupCurrencies;
    });

    const currencies = computed(() => {
      const currencies = [];
      const startAmounts = CashFloatModule.cashFloat?.startAmounts;
      for (const currency in startAmounts) {
        const currencyInfo = groupCurrencies.value.find(
          (c) => c.label === currency
        );
        if (currencyInfo) {
          currencies.push(currencyInfo);
        }
      }
      return currencies;
    });

    const selectedCashCurrencyLabel = computed(() => {
      return currency.value?.label || "";
    });

    const groupCurrency = computed(() => {
      const currency =
        currencies.value.find((c) => c.isDefault === 1) || currencies.value[0];
      return currency.label;
    });

    const paymentSettings = computed(() => {
      // Return only payment settings that are used
      return POSConfigModule.getPaymentSettings.filter((setting: any) => {
        return setting.used;
      });
    });

    const promocodeBeingChecked = computed(() => {
      // TODO: migrate to NOL when check is implemented
      // return BookingModule.promocodeBeingChecked;
      return false;
    });

    function selectdefaultPaymentMethod() {
      // We only support Cash with Refund, we don't force the user his default payment method in this case
      if (step.value !== BookingStep.Refund) {
        const usedPaymentMethod = getDefaultPaymentMethod();
        updatePaymentMethod(usedPaymentMethod.id);
      }
    }

    const formattedPaymentCurrency = computed(() => {
      return formatCurrency(
        selectedOrder.value?.orderSession?.currency as Currencies
      );
    });

    /**
     * The amount we display for the user to pay
     * For bookings that support split payment it is what is left to pay OR it is what the user enters
     * For normal bookings it is the sum of pss final values, if user enters an amount, then we show it
     */
    const displayedAmount = computed(() => {
      return (
        formatCurrency(paymentCurrency.value) +
        " " +
        (providedAmount.value || 0).toFixed(2)
      );
    });

    const whatIsLeftToPayText = computed(() => {
      return (
        formatCurrency(paymentCurrency.value) +
        " " +
        whatIsLeftToPay.value.toFixed(2)
      );
    });

    const whatIsLeftToPay = computed(() => {
      return totalBalance.value * (currency.value?.exchangeRate || 1);
    });

    const payInMultiCurrencies = ref(false);

    function toggleMultiCurrencies() {
      payInMultiCurrencies.value = !payInMultiCurrencies.value;
      if (!payInMultiCurrencies.value) {
        orderMetaStore.setCurrency(null);
        orderMetaStore.setProvidedAmount(totalBalance.value);
      } else {
        const currency = currencies.value.filter(
          (c) => c.label !== bookingPaymentCurrency.value
        )[0];
        orderMetaStore.setCurrency(currency);
        orderMetaStore.setProvidedAmount(
          totalBalance.value * currency.exchangeRate
        );
      }
    }

    function selectedCashCurrencyChanged(
      currencyLabel: string | number | boolean
    ) {
      const currency =
        currencies.value.find((c) => c.label === currencyLabel) || null;
      orderMetaStore.setCurrency(currency);
      orderMetaStore.setProvidedAmount(
        totalBalance.value * (currency?.exchangeRate || 1)
      );
    }

    function updatePaymentMethod(paymentMethodId: PaymentMethodTypes) {
      orderMetaStore.setPaymentMethod(
        paymentMethodToOrderPaymentMethod(paymentMethodId)
      );
      orderMetaStore.setStep(BookingStep.Payment);
    }

    function showCalculator() {
      ModalModule.setStatus(ModalStatus.PaymentCalculator);
    }

    function disablePaymentMethod(method: PaymentMethod): boolean {
      return method === PaymentMethod.DISCOUNT;
    }

    function displayDiscounts() {
      orderMetaStore.setStep(BookingStep.Discount);
    }

    function displayPromocodeArea() {
      orderMetaStore.setStep(BookingStep.Promocode);
    }

    function displayPromocodeCampaigns() {
      orderMetaStore.setStep(BookingStep.PromocodeCampaigns);
    }

    function displayOffers() {
      orderMetaStore.setStep(BookingStep.Offers);
    }

    const refundPaymentMethodOptions = computed(() => {
      // Offer only the methods the order was actually paid with, each capped
      // at its refundable remainder (maxValue, in cents). INCERT Voucher is
      // never offered: those refunds happen automatically when the confirmed
      // ticket is cancelled.
      const options = getRefundMethodOptions()
        .filter(
          (option) => option.method !== PaymentMethod.EXTERNAL_INCERT_VOUCHER
        )
        .map((option) => ({
          id: option.method,
          label:
            PaymentMethodToTranslatedString.value[option.method] ||
            option.method.replace(/_/g, " "),
          maxValue: option.maxValue,
        }));

      // Keep the terminal method at the bottom of the list (it may be disabled)
      return options.sort(
        (a, b) =>
          Number(a.id === PaymentMethod.POS_ADYEN) -
          Number(b.id === PaymentMethod.POS_ADYEN)
      );
    });

    // First method the cashier can actually select — skips disabled options
    function firstEnabledRefundMethod(
      options: { id: PaymentMethod; maxValue: number }[]
    ): PaymentMethod {
      const enabled = options.find(
        (option) => !isRefundPaymentMethodDisabled(option)
      );
      return (enabled ?? options[0])?.id ?? PaymentMethod.CASH;
    }

    // let's set the default payment method also for refund
    const refundPaymentMethod = ref<PaymentMethod>(
      firstEnabledRefundMethod(refundPaymentMethodOptions.value)
    );

    // Re-default the selection when the refundable methods change
    // (another order selected, or a refund settled)
    watch(refundPaymentMethodOptions, (options) => {
      if (!options.some((option) => option.id === refundPaymentMethod.value)) {
        refundPaymentMethod.value = firstEnabledRefundMethod(options);
      }
    });

    const isApplyRefundDisabled = computed(() => {
      // A refund is already being processed (terminal or backend call)
      if (isPaymentInProgress.value) {
        return true;
      }
      // Nothing left to refund (a pending online refund nets the balance to 0
      // through its obligation)
      if (Math.round(Math.abs(totalBalance.value) * 100) === 0) {
        return true;
      }
      const selectedOption = refundPaymentMethodOptions.value.find(
        (option) => option.id === refundPaymentMethod.value
      );
      return !selectedOption || isRefundPaymentMethodDisabled(selectedOption);
    });

    /**
     * Call appropriate refund function depending on payment method:
     * terminal refunds reverse on the device, manual refunds are recorded
     * locally (offline-capable), online refunds go through the backend.
     */
    async function handleRefund() {
      if (isApplyRefundDisabled.value) {
        return;
      }

      LogService.info(
        `Proceeding refund for order: ${selectedOrder.value?.id} with amount ${totalBalance.value} paymentMethod: ${refundMethod.value}`,
        { params: { order: selectedOrder.value } }
      );

      try {
        switch (refundPaymentMethod.value) {
          case PaymentMethod.POS_ADYEN:
            await proceedAdyenRefund();
            break;
          default:
            if (manualPaymentMethods.includes(refundPaymentMethod.value)) {
              // Manual refunds (cash, external card, ...) are recorded locally
              // and queued for sync — they work offline
              await proceedRegularRefund();
            } else {
              // Online card refunds are recorded server-side and settle
              // asynchronously via the Adyen webhook
              await proceedOnlineRefund();
            }
            break;
        }
      } catch {
        // journaled + surfaced by the order helpers; the refund is not applied
      }
    }

    const showDiscountButton = computed(() => {
      // Hide discount button for Chillon (L2-1009)
      if (POSConfigModule.groupId === 16268) {
        return false;
      }
      return true;
    });

    function isRefundPaymentMethodDisabled(paymentSetting: {
      id: PaymentMethod;
      maxValue: number;
    }) {
      try {
        if (
          paymentSetting.id === PaymentMethod.POS_ADYEN &&
          (!POSConfigModule.selectedConfig?.isRefundActivated ||
            !canRefundByTerminal())
        ) {
          return true;
        }
        // The method's refundable remainder can't cover the amount owed.
        // Both sides rounded: POS-created payments carry float cents
        // (e.g. 19.99 * 100 = 1998.9999...).
        return (
          Math.round(paymentSetting.maxValue) <
          Math.round(Math.abs(totalBalance.value) * 100)
        );
      } catch {
        // canRefundByTerminal throws when no order is selected
        return true;
      }
    }

    // Whenever the user resets paymentSettings we need to select a default paymentMethod
    watch(
      paymentSettings,
      () => {
        selectdefaultPaymentMethod();
      },
      { deep: true }
    );

    watch([step, paymentMethod], () => {
      if (!payInMultiCurrencies.value) {
        return;
      }

      if (
        paymentMethod.value !== PaymentMethod.CASH ||
        step.value !== BookingStep.Payment
      ) {
        // toggle off multi currency if not using cash or not in payment step
        toggleMultiCurrencies();
      }
    });

    // Keep the store in sync with the selected refund method
    // (immediate: the initial default must reach the store too)
    watch(
      refundPaymentMethod,
      () => {
        refundMethod.value = refundPaymentMethod.value;
      },
      { immediate: true }
    );

    return {
      t,
      whatIsLeftToPay,
      displayedAmount,
      formattedPaymentCurrency,
      showDiscount,
      paymentCurrency,
      step,
      BookingStep,
      updatePaymentMethod,
      showCalculator,
      disablePaymentMethod,
      Currencies,
      displayDiscounts,
      displayPromocodeArea,
      iconContent,
      iconAlt,
      iconStyle,
      cashPaymentMethod,
      cardAutoPaymentMethod,
      cardManualPaymentMethod,
      bankTransferPaymentMethod,
      InvoicePaymentMethod,
      isFree,
      isFullyPaidInPromocodeStep,
      isFullyPaidInDiscountStep,
      groupId,
      getLocale,
      whatIsLeftToPayText,
      ChequePaymentMethod,
      ChequeVacancesPaymentMethod,
      handleRefund,
      paymentSettings,
      paymentMethod,
      PaymentMethod,
      PaymentMethodToTranslatedString,
      paymentMethodToOrderPaymentMethod,
      CEPaymentMethod,
      OCCYGPaymentMethod,
      selectdefaultPaymentMethod,
      refundPaymentMethod,
      refundPaymentMethodOptions,
      isApplyRefundDisabled,
      PaymentMethodTypes,
      currencies,
      currency,
      groupCurrency,
      selectedCashCurrencyChanged,
      selectedCashCurrencyLabel,
      toggleMultiCurrencies,
      payInMultiCurrencies,
      bookingPaymentCurrency,
      hasMultiCurrenciesEnabled,
      showDiscountButton,
      displayPromocodeCampaigns,
      displayOffers,
      isRefundPaymentMethodDisabled,
      promocodeBeingChecked,

      selectedOrder,
      setProvidedAmount: orderMetaStore.setProvidedAmount,
      paymentOrderReference,
    };
  },
  async created() {
    // TODO: migrate to NOL when implemented
    // check availabilities before completing the booking
    await syncOrderAvailabilities();
  },
  async mounted() {
    this.selectdefaultPaymentMethod();
    const orderStore = useOrderStore();
    const orderMetaStore = useOrderMetaStore();

    // The currency toggle starts off, so drop a cash currency left from a previous visit
    orderMetaStore.setCurrency(null);
    orderMetaStore.setProvidedAmount(orderStore.selectedOrderSummary.balance);
  },
  beforeUnmount() {
    const orderStore = useOrderStore();
    const orderMetaStore = useOrderMetaStore();

    // Switching or opening a tab selects the other order before this unmounts;
    // the order meta setters target the selected order, so only reset the pay
    // currency and amount while this step's order is still the selected one
    if (orderStore.selectedOrder?.reference === this.paymentOrderReference) {
      orderMetaStore.setCurrency(null);
      orderMetaStore.setProvidedAmount(orderStore.selectedOrderSummary.balance);
    }
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.complete-order-wrapper {
  height: 100%;
  padding-bottom: 10px;

  .selected {
    border: none;
    border-bottom: 8px solid $color-dark-cyan;
    background-color: color-mix(
      in srgb,
      var(--primary-smeetz) 65%,
      transparent
    );
  }

  .selected-warning {
    border: none;
    border-bottom: 8px solid $color-danger-red;
    background-color: rgba($color: $color-danger-red, $alpha: 0.65);
  }

  .disabled-payment-option {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .payment-option {
    height: 50px;
    font-size: 16px;
    font-weight: 700;
    transition-duration: 0.5s;
    white-space: pre-wrap;
  }

  .payment-option:not(.disabled-payment-option, .selected-warning):hover {
    transition-duration: 0.5s;
    border: none;
    border-bottom: 8px solid $color-dark-cyan;
    background-color: color-mix(
      in srgb,
      var(--primary-smeetz) 65%,
      transparent
    );
  }

  .currency-button {
    padding: 25px 45px;
    margin: 3px 1px;
    border: none;
    background-color: $color-light-grey3;
    color: $color-text-primary;
    max-width: 120px;
  }

  .amount-button {
    margin: 1px 1px;
    border: none;
    background-color: $color-pacific-blue;
    font-weight: bold;
    color: $background-color-base;
    width: 120px;
    height: 50px;
  }

  .back-button {
    background-color: $color-grey1;
    // font-weight: bold;
    color: white;
  }

  .active {
    background-color: $color-pacific-blue;
    font-weight: bold;
    color: $background-color-base;
  }

  .order-amount {
    background-color: $color-light-grey3;
    color: $color-text-primary;
    padding: 30px 19%;
    margin: 3vh 0;
    border-radius: 4px;
    border: none;
    font-size: 16px;
  }

  .default-cursor {
    cursor: default;
  }

  .complete-button {
    background-color: $color-dark-cyan;
    color: $background-color-base;
    font-weight: bold;
    margin-top: 1vh;
    width: 98%;
    height: 50px;
  }

  .apply-button {
    margin-top: 3vh;
    width: 366px;
    height: 50px;
  }

  .complete-button-bg {
    background: $color-light-grey4;
  }

  p {
    font-weight: bold;
    text-align: center;
    margin-top: 20vh;
  }
  .apply-btn {
    margin-top: 50px;
    background-color: $color-swamp-green;
    color: $background-color-clear;
  }
  .light-text {
    margin: 0;
    color: rgba(0, 0, 0, 0.5);
    font-family: Helvetica;
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
}
</style>
